/**
 * HelpGuide - 도움말 가이드 패널
 *
 * WIA Book의 모든 기능을 카테고리별로 소개
 * 사이드바 또는 헤더의 "?" 버튼으로 열기
 *
 * 홍익인간 (Hongik Ingan) - WIA Standards
 */

import { useState } from 'react';
import { useI18n } from '../../i18n';
import {
  FiX,
  FiBook,
  FiSettings,
  FiVolume2,
  FiEdit3,
  FiSearch,
  FiMusic,
  FiRefreshCw,
  FiGlobe,
  FiHeart,
  FiChevronDown,
  FiChevronRight,
} from 'react-icons/fi';

interface HelpGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FeatureCategory {
  id: string;
  icon: React.ReactNode;
  titleKey: string;
  titleFallback: string;
  color: string;
  features: {
    titleKey: string;
    titleFallback: string;
    descKey: string;
    descFallback: string;
  }[];
}

const HelpGuide = ({ isOpen, onClose }: HelpGuideProps) => {
  const { t } = useI18n();
  const [expandedCategory, setExpandedCategory] = useState<string | null>('viewer');

  // 기능 카테고리 데이터
  const categories: FeatureCategory[] = [
    {
      id: 'viewer',
      icon: <FiBook size={20} />,
      titleKey: 'help.viewer.title',
      titleFallback: '뷰어 기능',
      color: 'text-blue-500',
      features: [
        {
          titleKey: 'help.viewer.formats',
          titleFallback: 'PDF + EPUB + Flipbook 통합',
          descKey: 'help.viewer.formatsDesc',
          descFallback: '세 가지 전자책 형식을 하나의 뷰어에서 읽을 수 있습니다.',
        },
        {
          titleKey: 'help.viewer.flipbook',
          titleFallback: '3D 플립북 모드',
          descKey: 'help.viewer.flipbookDesc',
          descFallback: '6가지 페이지 효과 (책, 잡지, 앨범, 페이드, 슬라이드, 카드)와 실감나는 책 넘김 소리.',
        },
        {
          titleKey: 'help.viewer.navigation',
          titleFallback: '다양한 네비게이션',
          descKey: 'help.viewer.navigationDesc',
          descFallback: '화살표 키, 스와이프, 클릭, 썸네일 바로 페이지 이동.',
        },
        {
          titleKey: 'help.viewer.fullscreen',
          titleFallback: '전체화면 & 인쇄',
          descKey: 'help.viewer.fullscreenDesc',
          descFallback: '몰입 독서를 위한 전체화면과 인쇄 기능.',
        },
      ],
    },
    {
      id: 'settings',
      icon: <FiSettings size={20} />,
      titleKey: 'help.settings.title',
      titleFallback: '설정 기능',
      color: 'text-orange-500',
      features: [
        {
          titleKey: 'help.settings.theme',
          titleFallback: '12가지 테마',
          descKey: 'help.settings.themeDesc',
          descFallback: '라이트 6종 + 다크 6종. 눈이 편한 테마를 선택하세요.',
        },
        {
          titleKey: 'help.settings.font',
          titleFallback: '글꼴 & 크기 조절',
          descKey: 'help.settings.fontDesc',
          descFallback: '5가지 글꼴, 10~32pt 크기, 1.0~2.5배 줄간격.',
        },
        {
          titleKey: 'help.settings.original',
          titleFallback: '원본 스타일 유지',
          descKey: 'help.settings.originalDesc',
          descFallback: '출판사가 지정한 원본 디자인 그대로 볼 수 있습니다.',
        },
      ],
    },
    {
      id: 'tts',
      icon: <FiVolume2 size={20} />,
      titleKey: 'help.tts.title',
      titleFallback: '소리내어 읽기',
      color: 'text-green-500',
      features: [
        {
          titleKey: 'help.tts.play',
          titleFallback: 'TTS 음성 합성',
          descKey: 'help.tts.playDesc',
          descFallback: 'PDF와 EPUB 모두 음성으로 읽어드립니다.',
        },
        {
          titleKey: 'help.tts.speed',
          titleFallback: '속도 조절',
          descKey: 'help.tts.speedDesc',
          descFallback: '0.5x ~ 2.0x까지 6단계 속도 조절.',
        },
      ],
    },
    {
      id: 'notes',
      icon: <FiEdit3 size={20} />,
      titleKey: 'help.notes.title',
      titleFallback: '메모 & 하이라이트',
      color: 'text-yellow-500',
      features: [
        {
          titleKey: 'help.notes.highlight',
          titleFallback: '5가지 색상 하이라이트',
          descKey: 'help.notes.highlightDesc',
          descFallback: '노랑, 초록, 파랑, 분홍, 보라 중 선택.',
        },
        {
          titleKey: 'help.notes.memo',
          titleFallback: '메모 추가',
          descKey: 'help.notes.memoDesc',
          descFallback: '하이라이트에 개인 메모를 함께 저장.',
        },
        {
          titleKey: 'help.notes.bookmark',
          titleFallback: '북마크',
          descKey: 'help.notes.bookmarkDesc',
          descFallback: '중요한 페이지를 저장하고 언제든 돌아오세요.',
        },
      ],
    },
    {
      id: 'search',
      icon: <FiSearch size={20} />,
      titleKey: 'help.search.title',
      titleFallback: '검색 & 목차',
      color: 'text-cyan-500',
      features: [
        {
          titleKey: 'help.search.text',
          titleFallback: '텍스트 검색',
          descKey: 'help.search.textDesc',
          descFallback: '책 전체에서 단어나 문장을 검색합니다.',
        },
        {
          titleKey: 'help.search.toc',
          titleFallback: '계층적 목차',
          descKey: 'help.search.tocDesc',
          descFallback: '중첩된 목차로 원하는 위치로 바로 이동.',
        },
      ],
    },
    {
      id: 'audio',
      icon: <FiMusic size={20} />,
      titleKey: 'help.audio.title',
      titleFallback: '배경 음악',
      color: 'text-pink-500',
      features: [
        {
          titleKey: 'help.audio.preset',
          titleFallback: '6가지 프리셋',
          descKey: 'help.audio.presetDesc',
          descFallback: '빗소리, 숲속, 카페, 벽난로, 파도, 밤.',
        },
        {
          titleKey: 'help.audio.custom',
          titleFallback: '나만의 음악',
          descKey: 'help.audio.customDesc',
          descFallback: 'MP3/WAV 파일을 업로드해서 들으세요.',
        },
      ],
    },
    {
      id: 'sync',
      icon: <FiRefreshCw size={20} />,
      titleKey: 'help.sync.title',
      titleFallback: '동기화 & 백업',
      color: 'text-purple-500',
      features: [
        {
          titleKey: 'help.sync.qr',
          titleFallback: 'QR 코드 이동',
          descKey: 'help.sync.qrDesc',
          descFallback: '다른 기기로 라이브러리를 간편하게 전송.',
        },
        {
          titleKey: 'help.sync.backup',
          titleFallback: 'JSON 백업',
          descKey: 'help.sync.backupDesc',
          descFallback: '책 정보, 하이라이트, 메모를 파일로 백업.',
        },
      ],
    },
    {
      id: 'global',
      icon: <FiGlobe size={20} />,
      titleKey: 'help.global.title',
      titleFallback: '다국어 & 접근성',
      color: 'text-indigo-500',
      features: [
        {
          titleKey: 'help.global.languages',
          titleFallback: '211개 언어',
          descKey: 'help.global.languagesDesc',
          descFallback: '전 세계 언어로 WIA Book를 사용하세요.',
        },
        {
          titleKey: 'help.global.a11y',
          titleFallback: '접근성',
          descKey: 'help.global.a11yDesc',
          descFallback: '키보드 네비게이션, 스크린 리더 지원.',
        },
      ],
    },
  ];

  const toggleCategory = (id: string) => {
    setExpandedCategory(expandedCategory === id ? null : id);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-purple-600 rounded-xl flex items-center justify-center">
              <FiHeart size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('help.title') || 'WIA Book 기능 가이드'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('help.subtitle') || '100+ 기능으로 최고의 독서 경험을'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <FiX size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {categories.map((category) => (
              <div
                key={category.id}
                className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className={category.color}>{category.icon}</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t(category.titleKey) || category.titleFallback}
                    </span>
                    <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full text-gray-600 dark:text-gray-400">
                      {category.features.length}
                    </span>
                  </div>
                  {expandedCategory === category.id ? (
                    <FiChevronDown size={20} className="text-gray-400" />
                  ) : (
                    <FiChevronRight size={20} className="text-gray-400" />
                  )}
                </button>

                {/* Features List */}
                {expandedCategory === category.id && (
                  <div className="px-4 pb-4 space-y-3">
                    {category.features.map((feature, index) => (
                      <div
                        key={index}
                        className="pl-8 border-l-2 border-gray-200 dark:border-gray-700"
                      >
                        <h4 className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                          {t(feature.titleKey) || feature.titleFallback}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t(feature.descKey) || feature.descFallback}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
          <p className="text-center text-xs text-gray-500 dark:text-gray-400">
            💜 {t('help.footer') || '홍익인간 (Hongik Ingan) - 널리 인간을 이롭게 하다'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpGuide;
